<template>
  <div class="appt-body min-w-[1000px]">
    <div class="actual-times">
      <div class="w-100 border-bottom">
        <h5 class="text-bold section-title mb-1">
          Actual Times
          <!-- Show edit icon in header for single-tech only -->
          <i
            v-if="!isMultiTechEnabled"
            class="far fa-edit link ml-1"
            data-test="edit-times-crew"
            @click="openCrewModal"
          />
        </h5>
      </div>
      <div :class="['grid-row', { prework: preWorkEnabled }, 'headings']">
        <span>Technician</span>
        <span v-if="preWorkEnabled">Accepted</span>
        <span v-if="preWorkEnabled"
          >Pre-Work
          <i
            :class="{ 'gray-arrow': isTravelTimesEmpty }"
            class="fas fa-arrow-right ml-2"
          />
        </span>
        <span>Travel Started</span>
        <span
          >Travel<i
            :class="{ 'gray-arrow': isTravelTimesEmpty }"
            class="fas fa-arrow-right ml-2"
        /></span>
        <span>Arrival</span>
        <span
          >Work<i
            :class="{ 'gray-arrow': isWorkTimesEmpty }"
            class="fas fa-arrow-right ml-2"
        /></span>
        <span>Departure</span>
        <span class="text-right">Duration</span>
      </div>

      <!-- Single loop — works for both single-tech and multi-tech -->
      <template
        v-for="(entry, techIndex) in techsForDisplay"
        :key="techRowKey(entry, techIndex)"
      >
        <div
          :class="[
            'grid-row',
            { prework: preWorkEnabled },
            'data',
            {
              'tech-group-gap':
                techIndex > 0 &&
                (techsForDisplay[techIndex - 1]?.crew_members || []).length > 0
            }
          ]"
        >
          <div class="tech-name-cont">
            <div v-if="entry.hasTech" class="d-flex align-items-center">
              <AppLink
                :to="technicianShowPath(entry.technician.user_id)"
                class="d-flex"
              >
                <tooltip
                  :disabled="!entry.isPinned"
                  placement="top"
                  class="full-width"
                >
                  <template #trigger>
                    <truncate-text :expand="false">{{
                      entry.techName
                    }}</truncate-text>
                  </template>
                  <template #tooltip>
                    <div><b>Pinned Reason: </b>{{ entry.pinnedReason }}</div>
                  </template>
                </tooltip>
                <tooltip
                  v-if="entry.techPhone"
                  placement="top"
                  :options="{ hideOnClick: true }"
                  class="full-width phone-icon ml-1"
                >
                  <template #trigger>
                    <i class="far fa-phone" />
                  </template>
                  <template #tooltip>
                    <div class="text-center">{{ entry.techPhone }}</div>
                  </template>
                </tooltip>
              </AppLink>
              <i
                v-if="isMultiTechEnabled"
                class="far fa-edit link ml-1"
                @click.stop.prevent="openCrewModal(entry.technician_profile_id)"
              />
            </div>
            <span v-else>{{ entry.techName }}</span>
          </div>
          <span v-if="preWorkEnabled">{{
            formatTime(entry.accepted_time)
          }}</span>
          <span v-if="preWorkEnabled" class="duration-cell">
            <minutes-label
              class="duration-display"
              :minutes="entry.pre_work_minutes"
              default-text="No Accepted or Travel Started Set"
            ></minutes-label>
          </span>
          <span>{{ formatTime(entry.dispatch_time) }}</span>
          <span class="duration-cell">
            <minutes-label
              class="duration-display"
              :minutes="entry.travel_minutes"
              default-text="No Arrival Time Set"
            ></minutes-label>
          </span>
          <span>{{ formatTime(entry.arrival_time) }}</span>
          <span class="duration-cell">
            <minutes-label
              class="duration-display"
              :minutes="entry.work_minutes"
              default-text="No Departure Time Set"
            ></minutes-label>
          </span>
          <span>{{ formatTime(entry.departure_time) }}</span>
          <span class="duration-cell">
            <minutes-label
              class="duration-display"
              :minutes="entry.duration_minutes"
              default-text="Appointment is Not Complete"
            ></minutes-label>
          </span>
        </div>

        <template v-if="entry.crew_members && entry.crew_members.length">
          <div class="subheader headings">Crew Members</div>
          <div
            v-for="(crew, i) in entry.crew_members"
            :key="`crew-${entry.technician_profile_id || techIndex}-${
              crew.id || 'new'
            }-${i}`"
            :class="['grid-row', { prework: preWorkEnabled }, 'data']"
          >
            <span class="d-flex mr-4">
              <truncate-text>{{ crew.full_name }}</truncate-text>
              <tooltip placement="top" :options="{ hideOnClick: true }">
                <template #trigger>
                  <i class="far fa-phone ml-2" />
                </template>
                <template #tooltip>
                  <div class="text-center">{{ crew.phone }}</div>
                </template>
              </tooltip>
            </span>
            <span v-if="preWorkEnabled">{{
              formatTime(crew.accepted_time)
            }}</span>
            <span v-if="preWorkEnabled" class="duration-cell">
              <minutes-label
                class="duration-display"
                :minutes="crew.pre_work_minutes"
                default-text="No Accepted or Travel Started Set"
              ></minutes-label>
            </span>
            <span>{{ formatTime(crew.dispatch_time) }}</span>
            <span class="duration-cell">
              <minutes-label
                class="duration-display"
                :minutes="crew.travel_minutes"
                default-text="No Arrival Time Set"
              ></minutes-label>
            </span>
            <span>{{ formatTime(crew.arrival_time) }}</span>
            <span class="duration-cell">
              <minutes-label
                class="duration-display"
                :minutes="crew.work_minutes"
                default-text="No Departure Time Set"
              ></minutes-label>
            </span>
            <span>{{ formatTime(crew.departure_time) }}</span>
            <span class="duration-cell">
              <minutes-label
                class="duration-display"
                :minutes="crew.duration_minutes"
              ></minutes-label>
            </span>
          </div>
        </template>
      </template>
      <template v-if="otherCrewMembers.length">
        <div class="subheader headings">Other crew</div>
        <div
          v-for="(crew, i) in otherCrewMembers"
          :key="`other-crew-${crew.id}-${i}`"
          :class="['grid-row', { prework: preWorkEnabled }, 'data']"
        >
          <span class="d-flex mr-4">
            <truncate-text>{{ crew.full_name }}</truncate-text>
            <tooltip placement="top" :options="{ hideOnClick: true }">
              <template #trigger>
                <i class="far fa-phone ml-2" />
              </template>
              <template #tooltip>
                <div class="text-center">{{ crew.phone }}</div>
              </template>
            </tooltip>
          </span>
          <span v-if="preWorkEnabled">{{
            formatTime(crew.accepted_time)
          }}</span>
          <span v-if="preWorkEnabled" class="duration-cell">
            <minutes-label
              class="duration-display"
              :minutes="crew.pre_work_minutes"
              default-text="No Accepted or Travel Started Set"
            ></minutes-label>
          </span>
          <span>{{ formatTime(crew.dispatch_time) }}</span>
          <span class="duration-cell">
            <minutes-label
              class="duration-display"
              :minutes="crew.travel_minutes"
              default-text="No Arrival Time Set"
            ></minutes-label>
          </span>
          <span>{{ formatTime(crew.arrival_time) }}</span>
          <span class="duration-cell">
            <minutes-label
              class="duration-display"
              :minutes="crew.work_minutes"
              default-text="No Departure Time Set"
            ></minutes-label>
          </span>
          <span>{{ formatTime(crew.departure_time) }}</span>
          <span class="duration-cell">
            <minutes-label
              class="duration-display"
              :minutes="crew.duration_minutes"
            ></minutes-label>
          </span>
        </div>
      </template>
      <div
        v-if="showTotals"
        :class="['totals-grid', { prework: preWorkEnabled }]"
      >
        <span>Totals</span>
        <div v-if="preWorkEnabled"></div>
        <span v-if="preWorkEnabled" class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="appointment.total_pre_work_minutes"
          ></minutes-label>
        </span>
        <span></span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="appointment.total_travel_minutes"
          ></minutes-label>
        </span>
        <span></span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="appointment.total_work_minutes"
          ></minutes-label>
        </span>
        <span></span>
        <span class="duration-cell">
          <minutes-label
            class="duration-display"
            :minutes="
              isMultiTechEnabled
                ? totalDurationMinutesForDisplay
                : appointment.total_duration_minutes
            "
          ></minutes-label>
        </span>
      </div>
    </div>
    <TechnicianTimeEditModal
      v-if="crewEditModal"
      :key="crewModalKey"
      :appointment="appointment"
      :appointment-technician-entry="selectedTechnicianEntry"
      :technician-profile-id="selectedTechnicianProfileId"
      @close="hideCrewModal"
      @refresh-appointment="hideCrewModal"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import {
  buildTechniciansById,
  getProperty,
  isFalsy,
  normalizeId
} from "@/lib/helpers.js";

import AppLink from "@/components/Link.vue";
import MinutesLabel from "@/components/MinutesLabel.vue";
import Tooltip from "@/components/Tooltip.vue";
import TruncateText from "@/components/TruncateText.vue";
import TechnicianTimeEditModal from "@/AppointmentDetails/TechnicianTimeEditModal.vue";

export default {
  components: {
    Tooltip,
    TechnicianTimeEditModal,
    AppLink,
    MinutesLabel,
    TruncateText
  },
  props: {
    appointment: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      crewEditModal: false,
      selectedTechnicianProfileId: null
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant", "settingEnabledForTenant"]),
    preWorkEnabled() {
      return this.settingEnabledForTenant("pre_work_enabled");
    },
    isMultiTechEnabled() {
      return this.featureEnabledForTenant("multiple_technicians_ff_enabled");
    },
    appointmentTechnicians() {
      return (
        Array.isArray(this.appointment.appointment_technicians)
          ? [...this.appointment.appointment_technicians]
          : []
      )
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map((appointmentTechnician) => ({
          ...appointmentTechnician,
          technician_profile_id: normalizeId(
            appointmentTechnician.technician_profile_id
          )
        }));
    },
    techniciansById() {
      return buildTechniciansById(this.appointment);
    },
    appointmentTechniciansForDisplay() {
      return this.appointmentTechnicians.map((appointmentTechnician) => ({
        ...appointmentTechnician,
        technician:
          this.techniciansById.get(
            appointmentTechnician.technician_profile_id
          ) || null
      }));
    },

    techsForDisplay() {
      const buildEntry = (tech, source, crewMembers) => {
        const isPinned = source.assignment_pinned ?? false;
        const fullName = getProperty(tech, "full_name");
        const techName = fullName
          ? `${fullName}${isPinned ? " (Pinned)" : ""}`
          : "--";
        const entryTimes = {
          accepted_time: source.accepted_time,
          dispatch_time: source.dispatch_time,
          arrival_time: source.arrival_time,
          departure_time: source.departure_time,
          pre_work_minutes: source.pre_work_minutes,
          travel_minutes: source.travel_minutes,
          work_minutes: source.work_minutes,
          duration_minutes: source.duration_minutes
        };
        return {
          technician: tech,
          technician_profile_id:
            normalizeId(source.technician_profile_id) ||
            normalizeId(getProperty(tech, "technician_profile_id")) ||
            normalizeId(getProperty(tech, "technician_id")) ||
            normalizeId(getProperty(tech, "id")),
          hasTech: !!fullName,
          techName,
          techPhone: getProperty(tech, "phone") || "",
          isPinned,
          pinnedReason: source.pinned_reason || "N/A",
          ...entryTimes,
          // FF ON: match legacy behavior — only inherit when crew uses leader times
          crew_members: (crewMembers || []).map((crew) => ({
            ...crew,
            _times: crew.use_crew_leader_times
              ? entryTimes
              : {
                  accepted_time: crew.accepted_time,
                  dispatch_time: crew.dispatch_time,
                  arrival_time: crew.arrival_time,
                  departure_time: crew.departure_time,
                  pre_work_minutes: crew.pre_work_minutes,
                  travel_minutes: crew.travel_minutes,
                  work_minutes: crew.work_minutes,
                  duration_minutes: crew.duration_minutes
                }
          }))
        };
      };

      if (
        this.isMultiTechEnabled &&
        this.appointmentTechniciansForDisplay.length
      ) {
        return this.appointmentTechniciansForDisplay.map(
          (appointmentTechnician) => {
            const crewMembers = this.crewForTech(
              appointmentTechnician.technician_profile_id
            );
            return buildEntry(
              appointmentTechnician.technician,
              appointmentTechnician,
              crewMembers
            );
          }
        );
      }

      // FF OFF: legacy single-tech behavior — use stored minute fields directly,
      // never calculate from timestamps.
      const tech =
        this.appointment.technician ||
        this.appointmentTechniciansForDisplay[0]?.technician ||
        null;
      const isPinned = this.appointment.assignment_pinned ?? false;
      const fullName = getProperty(tech, "full_name");
      const entryTimes = {
        accepted_time: this.appointment.accepted_time,
        dispatch_time: this.appointment.dispatch_time,
        arrival_time: this.appointment.arrival_time,
        departure_time: this.appointment.departure_time,
        pre_work_minutes: this.appointment.pre_work_minutes,
        travel_minutes: this.appointment.travel_minutes,
        work_minutes: this.appointment.work_minutes,
        duration_minutes: this.appointment.duration_minutes
      };
      return [
        {
          technician: tech,
          technician_profile_id:
            normalizeId(getProperty(tech, "technician_profile_id")) ||
            normalizeId(getProperty(tech, "technician_id")) ||
            normalizeId(getProperty(tech, "id")),
          hasTech: !!fullName,
          techName: fullName
            ? `${fullName}${isPinned ? " (Pinned)" : ""}`
            : "--",
          techPhone: getProperty(tech, "phone") || "",
          isPinned,
          pinnedReason: this.appointment.pinned_reason || "N/A",
          ...entryTimes,
          // FF OFF: crew respects use_crew_leader_times to follow tech's times
          crew_members: (this.appointment.crew_members || []).map((crew) => ({
            ...crew,
            _times: crew.use_crew_leader_times
              ? entryTimes
              : {
                  accepted_time: crew.accepted_time,
                  dispatch_time: crew.dispatch_time,
                  arrival_time: crew.arrival_time,
                  departure_time: crew.departure_time,
                  pre_work_minutes: crew.pre_work_minutes,
                  travel_minutes: crew.travel_minutes,
                  work_minutes: crew.work_minutes,
                  duration_minutes: crew.duration_minutes
                }
          }))
        }
      ];
    },
    otherCrewMembers() {
      if (
        !this.isMultiTechEnabled ||
        !this.appointmentTechniciansForDisplay.length
      ) {
        return [];
      }

      const assignedTechnicianIds = new Set(
        this.appointmentTechniciansForDisplay
          .map((appointmentTechnician) =>
            normalizeId(appointmentTechnician.technician_profile_id)
          )
          .filter((id) => id != null)
      );

      return (this.appointment.crew_members || []).filter((crew) => {
        const assignedToId = normalizeId(crew?.assigned_to?.technician_id);
        const apptScopedId = normalizeId(crew?.assigned_technician_profile_id);
        if (apptScopedId != null && assignedTechnicianIds.has(apptScopedId)) {
          return false;
        }
        return assignedToId == null || !assignedTechnicianIds.has(assignedToId);
      });
    },

    showTotals() {
      const {
        total_travel_minutes,
        total_work_minutes,
        total_duration_minutes
      } = this.appointment;
      if (!this.isMultiTechEnabled) {
        // Legacy: all three stored totals must be present
        return (
          !isFalsy(total_travel_minutes) &&
          !isFalsy(total_work_minutes) &&
          !isFalsy(total_duration_minutes)
        );
      }
      return (
        !isFalsy(total_travel_minutes) &&
        !isFalsy(total_work_minutes) &&
        !isFalsy(this.totalDurationMinutesForDisplay)
      );
    },
    totalDurationMinutesForDisplay() {
      // Only used when FF is ON — falls back when total_duration_minutes absent.
      const {
        total_pre_work_minutes,
        total_travel_minutes,
        total_work_minutes,
        total_duration_minutes,
        duration_minutes
      } = this.appointment;

      if (!isFalsy(total_duration_minutes)) {
        return total_duration_minutes;
      }

      if (
        !isFalsy(total_pre_work_minutes) ||
        !isFalsy(total_travel_minutes) ||
        !isFalsy(total_work_minutes)
      ) {
        return (
          (Number(total_pre_work_minutes) || 0) +
          (Number(total_travel_minutes) || 0) +
          (Number(total_work_minutes) || 0)
        );
      }

      return duration_minutes;
    },
    isTravelTimesEmpty() {
      if (!this.isMultiTechEnabled) {
        const { travel_minutes, crew_members } = this.appointment;
        return (
          isFalsy(travel_minutes) &&
          (crew_members || []).every((m) => isFalsy(m.travel_minutes))
        );
      }
      return (
        this.techsForDisplay.every(
          (e) =>
            isFalsy(e.travel_minutes) &&
            (e.crew_members || []).every((m) =>
              isFalsy(m._times.travel_minutes)
            )
        ) && this.otherCrewMembers.every((m) => isFalsy(m.travel_minutes))
      );
    },
    isWorkTimesEmpty() {
      if (!this.isMultiTechEnabled) {
        const { work_minutes, crew_members } = this.appointment;
        return (
          isFalsy(work_minutes) &&
          (crew_members || []).every((m) => isFalsy(m.work_minutes))
        );
      }
      return (
        this.techsForDisplay.every(
          (e) =>
            isFalsy(e.work_minutes) &&
            (e.crew_members || []).every((m) => isFalsy(m._times.work_minutes))
        ) && this.otherCrewMembers.every((m) => isFalsy(m.work_minutes))
      );
    },
    crewModalKey() {
      return `crew-modal-${this.appointment?.id || "unknown"}-${
        this.selectedTechnicianProfileId ?? "single"
      }`;
    },
    selectedTechnicianEntry() {
      return (this.appointment.appointment_technicians || []).find(
        (at) =>
          normalizeId(at.technician_profile_id) ===
          this.selectedTechnicianProfileId
      );
    }
  },
  methods: {
    techRowKey(entry, techIndex) {
      return `tech-${
        entry.technician_profile_id || entry.techName || techIndex
      }`;
    },
    crewForTech(technicianProfileId) {
      const techId = normalizeId(technicianProfileId);
      return (this.appointment.crew_members || []).filter((crew) => {
        const apptScopedId = normalizeId(crew?.assigned_technician_profile_id);
        if (apptScopedId != null) {
          return apptScopedId === techId;
        }
        return normalizeId(crew?.assigned_to?.technician_id) === techId;
      });
    },
    openCrewModal(technicianProfileId = null) {
      this.selectedTechnicianProfileId =
        normalizeId(technicianProfileId) ||
        this.appointmentTechnicians[0]?.technician_profile_id ||
        null;
      this.crewEditModal = true;
    },
    hideCrewModal() {
      this.crewEditModal = false;
      this.selectedTechnicianProfileId = null;
      EventBus.$emit("refresh-job-notes");
    },
    formatTime(time) {
      if (!time) {
        return "--";
      }
      return this.formatDateTimeWithTimezone(
        time,
        this.appointment.address.time_zone
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.actual-times {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: var(--light-color);
  width: 100%;
  padding: 12px;
  border-radius: 5px;
}

.appt-body {
  display: flex;
  justify-content: space-between;
}

.time-display {
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  align-items: flex-start;
}

.grid-headers > b > i {
  margin-right: 5px;
}

.grid-row {
  display: grid;
  width: 100%;
  padding: 0 8px;
  grid: 1fr / 15% repeat(5, 1fr) 7%;
  &.prework {
    grid: 1fr / 15% repeat(7, 1fr) 7%;
  }
  &.headings > span {
    font-weight: 500;
  }
  &.data > * {
    font-weight: 600;
  }
}

.subheader {
  width: 100%;
  margin-top: 1rem;
  padding-left: 8px;
  &.headings {
    font-weight: 500;
  }
}

.section-title {
  font-size: 1rem;
  padding-left: 8px;
}

.totals-grid {
  margin-top: 1rem;
  display: grid;
  width: 100%;
  grid: 1fr / 15% repeat(5, 1fr) 7%;
  padding: 8px;
  background-color: white;
  border: 1px solid var(--gray-border-color);
  border-radius: 8px;
  font-weight: bold;
  &.prework {
    grid: 1fr / 15% repeat(7, 1fr) 7%;
  }
}

.duration-cell {
  display: flex;
  text-align: end;
  align-items: center;
  i {
    margin-top: 4px;
  }
  .duration-display {
    width: 60px;
    white-space: nowrap;
  }
  &:last-child {
    justify-content: flex-end;
  }
}

.gray-arrow {
  color: var(--secondary-color);
}
.tech-name-cont {
  width: 80%;
  // A long technician name can crowd the trailing edit icon against the
  // neighbouring time cell in this narrow grid column; keep the icon on top
  // so it stays clickable instead of being occluded by the adjacent value.
  .fa-edit {
    position: relative;
    z-index: 1;
  }
}
.phone-icon {
  color: var(--font-color);
}

.tech-group-gap {
  margin-top: 8px;
}
</style>
